import { BorderBox, Position, Text } from '@trmediaab/zebra';
import { forwardRef } from 'react';

export interface ButtonStyleProps {
  size?: any;
  bg?: any;
  checkedColor: any;
  checkedSizeFactor?: number;
  borderWidth?: any;
  borderColor?: any;
  borderRadius?: any;
  boxShadow?: any;
}

export type LabelStyleProps = Pick<
  React.ComponentProps<typeof Text>,
  'fontSize' | 'fontWeight' | 'color' | 'width'
>;

export interface RadioInputProps extends React.ComponentPropsWithRef<'input'> {
  label: string;
  gap?: any;
  containerStyles?: React.ComponentProps<typeof BorderBox>;
  buttonStyles?: ButtonStyleProps;
  labelStyles?: LabelStyleProps;
}

/**
 * A component for rendering a custom radio input.
 */
const RadioInput = forwardRef<HTMLDivElement, RadioInputProps>((props, ref) => {
  const {
    checked,
    id,
    name,
    value,
    disabled = false,
    label,
    gap = 2,
    containerStyles,
    buttonStyles,
    labelStyles,
    onChange,
  } = props;

  const {
    size: buttonSize = 'space.3',
    bg: buttonBg = 'white',
    borderWidth: buttonBorderWidth = 1,
    borderColor: buttonBorderColor = 'border.primary',
    borderRadius: buttonRadius = 'circle',
    boxShadow: buttonShadow,
    checkedColor,
    checkedSizeFactor = 0.6,
  } = buttonStyles || {};

  return (
    <BorderBox
      {...containerStyles}
      sx={{ opacity: disabled ? '0.6' : undefined }}
      ref={ref}
    >
      <Position
        as="input"
        type="radio"
        name={name}
        id={id}
        value={value}
        checked={checked}
        disabled={disabled}
        sx={{
          appearance: 'none',
          '&:checked + label': {
            '&::after': {
              visibility: 'visible',
            },
          },
        }}
        onChange={onChange}
      />

      <Text
        as="label"
        htmlFor={id}
        {...labelStyles}
        display="inline-flex"
        alignItems="center"
        sx={{
          position: 'relative',
          '&::before': {
            content: "''",
            display: 'inline-block',
            flexShrink: 0,
            size: buttonSize,
            bg: buttonBg,
            borderWidth: buttonBorderWidth,
            borderColor: buttonBorderColor,
            borderStyle: 'solid',
            borderRadius: buttonRadius,
            boxShadow: buttonShadow,
            mr: gap,
          },
          '&::after': {
            content: "''",
            display: 'inline-block',
            flexShrink: 0,
            position: 'absolute',
            visibility: 'hidden',
            size: buttonSize,
            bg: checkedColor,
            borderRadius: buttonRadius,
            transform: `scale(${checkedSizeFactor})`,
          },
        }}
      >
        {label}
      </Text>
    </BorderBox>
  );
});

export default RadioInput;
